import { FC, memo } from 'react'
import { IconButton, ModalFooter, ModalHeader } from '@cargomart/ui'
import { FloatingOverlay } from '@floating-ui/react'
import cn from 'classnames'
import { useIsMobile } from 'hooks/media'
import IconArrowLeft from 'remixicon/icons/Arrows/arrow-left-line.svg?react'
import IconClose from 'remixicon/icons/System/close-fill.svg?react'

import { Modal } from '../Modal/Modal'
import { ModalSimpleProps } from './types'

const ModalSimple: FC<ModalSimpleProps> = (props) => {
  const {
    hasHeader = true,
    isCentered = true,
    title,
    bodyClassName,
    headerClassName,
    footerClassName,
    isStickyHeader,
    isStickyFooter,
    isCloseButton = true,
    isCloseOnOutsideClick = true,
    headerAdditional,
    mobileModalFooter,
    modalFooter,
    children,
    onClose,
    mobileHeaderAdditional,
    id,
    size,
    testId,
    titleTestId
  } = props
  const isMobile = useIsMobile()

  return (
    <FloatingOverlay lockScroll className="z-modal bg-[#000]/[0.3] [&>div]:[&>div]:h-full">
      {isMobile ? (
        <Modal isCloseOnEsc isMobile id={id} isCloseOnOutsideClick={isCloseOnOutsideClick} testId={testId}>
          <ModalHeader
            isBgSurface
            isSticky
            tabIndex={0}
            className={cn(headerClassName, 'flex items-center justify-between', {
              'px-2 pt-2': mobileHeaderAdditional,
              'p-2': !mobileHeaderAdditional
            })}
          >
            <div className="flex w-full flex-col">
              <div className="flex items-center">
                <IconButton className="mr-2" onClick={onClose}>
                  <IconArrowLeft containerSize="lg" size="sm" />
                </IconButton>
                <div className="text-title-md" data-test-id={titleTestId}>
                  {title}
                </div>
              </div>
              {mobileHeaderAdditional}
            </div>
          </ModalHeader>

          <div className={cn('flex-grow px-4 py-2', bodyClassName)}>{children}</div>

          {mobileModalFooter && (
            <ModalFooter isSticky className={cn(footerClassName, 'p-2')} isBgSurface={false}>
              {mobileModalFooter}
            </ModalFooter>
          )}
        </Modal>
      ) : (
        <Modal
          isCloseOnEsc
          className="rounded-xl !outline-none"
          id={id}
          isCloseOnOutsideClick={isCloseOnOutsideClick}
          isDialogType={isCentered}
          onClose={onClose}
          size={size}
          testId={testId}
        >
          {hasHeader && (
            <ModalHeader
              isBgSurface
              isSticky={isStickyHeader}
              tabIndex={0}
              className={cn(
                headerClassName,
                'flex min-h-[72px] flex-col gap-x-4 rounded-tl-xl rounded-tr-xl py-4 pl-6 pr-4'
              )}
            >
              <div className="flex items-center justify-between">
                <div className="break-all text-headline-md" data-test-id={titleTestId}>
                  {title}
                </div>
                {isCloseButton && (
                  <IconButton onClick={onClose} size="lg">
                    <IconClose size="nm" />
                  </IconButton>
                )}
              </div>
              {headerAdditional}
            </ModalHeader>
          )}

          <div className={cn('px-6', bodyClassName)}>{children}</div>

          {modalFooter && (
            <ModalFooter
              isBgSurface
              className={cn(footerClassName, 'rounded-bl-xl rounded-br-xl p-6')}
              isSticky={isStickyFooter}
            >
              {modalFooter}
            </ModalFooter>
          )}
        </Modal>
      )}
    </FloatingOverlay>
  )
}

export default memo(ModalSimple)
